import translationKeys from '../translations/keys';
import { createElement, ReactNode } from 'react';

import { PiWarningDiamondFill, PiCircleHalfFill, PiCheckCircle } from "react-icons/pi";
import { AMBER, SUPPORT_RED, VL_GREEN, VL_RED } from './colours';

import { ReactComponent as NoVideoIcon } from '../assets/images/icons/cameras.svg';
import { ReactComponent as CloudRecordingOffIcon } from '../assets/images/icons/cloud.svg';
import { ReactComponent as UninstalledIcon } from '../assets/images/icons/uninstall.svg';
import { ReactComponent as UnauthorisedIcon } from '../assets/images/icons/lock.svg';
import { ReactComponent as UnreachableIcon } from '../assets/images/icons/cross.svg';
import { ReactComponent as OfflineIcon } from '../assets/images/icons/prohibition.svg';
import { ReactComponent as StandbyIcon } from '../assets/images/icons/standby.svg';
import { ReactComponent as OnlineIcon } from '../assets/images/icons/tick.svg';
import _ from 'lodash';

export const CAMERA_STATUSES = {
    // errors
    offline: 'offline',
    novideo: 'novideo',
    unreachable: 'unreachable',
    unauthorised: 'unauthorised',

    // warnings
    standby: 'standby',
    uninstalled: 'uninstalled',
    cloudrecordingoff: 'cloudrecordingoff',

    // ok
    online: 'online',
} as const;

export const VIDEO_STATUSES = {
    LIVE: 'live',
    LOADING: 'loading',
    BUFFERING: 'buffering',
    VIDEO_UNPLAYABLE: 'videoUnplayable'
} as const;

export const STATUS_TYPES = {
    OK: 'ok',
    WARNING: 'warning',
    ERROR: 'error'
} as const;

export type CameraStatusType = typeof CAMERA_STATUSES[keyof typeof CAMERA_STATUSES];
export type VideoStatus = typeof VIDEO_STATUSES[keyof typeof VIDEO_STATUSES];
export type CameraUIState = CameraStatusType | VideoStatus;

export type StatusType = typeof STATUS_TYPES[keyof typeof STATUS_TYPES];

export const STATUS_TRANSLATIONS: Record<CameraUIState, string> = {
    // Video statuses
    [VIDEO_STATUSES.LIVE]: translationKeys.cameras.status.LIVE,
    [VIDEO_STATUSES.LOADING]: translationKeys.cameras.status.LOADING,
    [VIDEO_STATUSES.BUFFERING]: translationKeys.cameras.liveStream.buffering.BUFFERING,
    [VIDEO_STATUSES.VIDEO_UNPLAYABLE]: translationKeys.cameras.status.VIDEO_UNSUPPORTED,
    // Camera statuses
    [CAMERA_STATUSES.online]: translationKeys.cameras.status.ONLINE,
    [CAMERA_STATUSES.standby]: translationKeys.cameras.status.STANDBY,
    [CAMERA_STATUSES.novideo]: translationKeys.cameras.status.NO_VIDEO,
    [CAMERA_STATUSES.offline]: translationKeys.cameras.status.OFFLINE,
    [CAMERA_STATUSES.uninstalled]: translationKeys.cameras.status.UNINSTALLED,
    [CAMERA_STATUSES.unreachable]: translationKeys.cameras.status.UNREACHABLE,
    [CAMERA_STATUSES.unauthorised]: translationKeys.cameras.status.UNAUTHORISED,
    [CAMERA_STATUSES.cloudrecordingoff]: translationKeys.cameras.status.CLOUD_RECORDING_OFF,
};

export const getStatusTranslation = (t: (key: string) => string, status: CameraUIState): string => {
    const translationKey = STATUS_TRANSLATIONS[status];
    return t(translationKey);
};

export const getStatusType = (status: CameraUIState): StatusType => {
    switch (status) {
        case CAMERA_STATUSES.online:
        case VIDEO_STATUSES.LIVE:
            return STATUS_TYPES.OK;
        case CAMERA_STATUSES.standby:
        case CAMERA_STATUSES.uninstalled:
        case CAMERA_STATUSES.cloudrecordingoff:
            return STATUS_TYPES.WARNING;
        default:
            return STATUS_TYPES.ERROR;
    }
};

export const getCameraStatus = (camera: { status: CameraStatusType; cloudRecordingEnabled: boolean, camId: string, cloudAdapterId?: string }): CameraUIState => {
    const cloudRecordingOff = !camera.cloudRecordingEnabled;
    if (cloudRecordingOff && getStatusType(camera.status) !== STATUS_TYPES.ERROR) {
        return CAMERA_STATUSES.cloudrecordingoff;
    }
    if (camera.status === CAMERA_STATUSES.offline && camera.camId.endsWith('-CA') && !camera.cloudAdapterId) {
        return CAMERA_STATUSES.uninstalled;
    }
    if (_.values(CAMERA_STATUSES).includes(camera.status)) {
        return camera.status;
    }
    return CAMERA_STATUSES.offline;
};

export const getStatusTypeIcon = (type: StatusType): ReactNode => {
    switch (type) {
        case STATUS_TYPES.OK:
            return createElement(PiCheckCircle, { color: VL_GREEN });
        case STATUS_TYPES.WARNING:
            return createElement(PiCircleHalfFill, { color: AMBER });
        case STATUS_TYPES.ERROR:
            return createElement(PiWarningDiamondFill, { color: VL_RED } );
        default:
            return null;
    }
};

export const getStatusTypeColour = (type: StatusType): string => {
    switch (type) {
        case STATUS_TYPES.OK:
            return VL_GREEN;
        case STATUS_TYPES.WARNING:
            return AMBER;
        case STATUS_TYPES.ERROR:
            return SUPPORT_RED;
        default:
            return 'transparent';
    }
};

/**
 * Calculate status ratios for cameras showing counts and percentages
 * @param {Object} cameras - Object mapping uidd to camera info (including status and cloudRecordingEnabled)
 * @returns {Record<string, { count: number; percentage: number }>} - Object with status as key and count/percentage as values
 */
export const getStatusRatios = (cameras: Record<string, { id?: string, status: CameraStatusType; cloudRecordingEnabled?: boolean, camId?: string, cloudAdapterId?: string }>): Record<string, { count: number; percentage: number }> => {
    if (!cameras || _.isEmpty(cameras)) {
        return {};
    }
    
    // Count cameras by status
    const statusCounts: Record<string, number> = {};
    _.forEach(cameras, (camera) => {
        if (!camera || !camera.camId || (camera.id && camera.id.includes("NO_CAMS"))) {
            return;
        }
        const cameraStatus = camera.status;
        const cloudRecordingEnabled = camera.cloudRecordingEnabled !== false; // default to true if undefined
        const status = getCameraStatus({ status: cameraStatus, cloudRecordingEnabled, camId: camera.camId, cloudAdapterId: camera.cloudAdapterId });
        statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const totalCameras = _.values(statusCounts).reduce((sum, count) => sum + count, 0);
    
    if (totalCameras === 0) {
        return {};
    }

    // Build result object with count and percentage in the specified order
    const statusRatios: Record<string, { count: number; percentage: number }> = {};
    
    // Iterate through CAMERA_STATUSES in order
    _.forEach(CAMERA_STATUSES, (statusKey) => {
        const count = statusCounts[statusKey];
        if (count !== undefined) {
            const percentage = (count / totalCameras) * 100;
            statusRatios[statusKey] = {
                count,
                percentage
            };
        }
    });

    return statusRatios;
};
export interface CameraStatusInfo {
    type: StatusType;
    label: string;
    description: string;
    liveStreaming: StatusType;
    recording: StatusType;
    cloud: StatusType | [StatusType, string];
    adapter: StatusType | [StatusType, string];
    camera: StatusType | [StatusType, string];
    troubleshooting: string[];
    icon: React.ComponentType<any>;
};

export const cameraStatusInfo: Record<CameraStatusType, CameraStatusInfo> = {
    [CAMERA_STATUSES.online]: { 
        type: STATUS_TYPES.OK,
        label: translationKeys.cameras.status.ONLINE,
        description: translationKeys.cameras.statusDescriptions.ONLINE,
        liveStreaming: STATUS_TYPES.OK,
        recording: STATUS_TYPES.OK,
        cloud: STATUS_TYPES.OK,
        adapter: STATUS_TYPES.OK,
        camera: STATUS_TYPES.OK,
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.ONLINE_1,
        ],
        icon: OnlineIcon,
    },
    [CAMERA_STATUSES.standby]: { 
        type: STATUS_TYPES.WARNING,
        label: translationKeys.cameras.status.STANDBY,
        description: translationKeys.cameras.statusDescriptions.STANDBY,
        liveStreaming: STATUS_TYPES.ERROR,
        recording: STATUS_TYPES.ERROR,
        cloud: STATUS_TYPES.OK,
        adapter: STATUS_TYPES.OK,
        camera: [STATUS_TYPES.WARNING, translationKeys.cameras.warningDescriptions.USER_DISABLED],
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.STANDBY_1,
            translationKeys.cameras.statusTroubleshooting.STANDBY_2,
        ],
        icon: StandbyIcon,
    },
    [CAMERA_STATUSES.novideo]: { 
        type: STATUS_TYPES.ERROR,
        label: translationKeys.cameras.status.NO_VIDEO,
        description: translationKeys.cameras.statusDescriptions.NO_VIDEO,
        liveStreaming: STATUS_TYPES.ERROR,
        recording: STATUS_TYPES.ERROR,
        cloud: STATUS_TYPES.OK,
        adapter: STATUS_TYPES.OK,
        camera: [STATUS_TYPES.ERROR, translationKeys.cameras.warningDescriptions.NO_VIDEO],
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.NO_VIDEO_1,
            translationKeys.cameras.statusTroubleshooting.NO_VIDEO_2,
        ],
        icon: NoVideoIcon,
    },
    [CAMERA_STATUSES.offline]: { 
        type: STATUS_TYPES.ERROR,
        label: translationKeys.cameras.status.OFFLINE,
        description: translationKeys.cameras.statusDescriptions.OFFLINE,
        liveStreaming: STATUS_TYPES.ERROR,
        recording: STATUS_TYPES.ERROR,
        cloud: STATUS_TYPES.ERROR,
        adapter: STATUS_TYPES.ERROR,
        camera: STATUS_TYPES.ERROR,
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.OFFLINE_1,
            translationKeys.cameras.statusTroubleshooting.OFFLINE_2,
            translationKeys.cameras.statusTroubleshooting.OFFLINE_3,
        ],
        icon: OfflineIcon,
    },
    [CAMERA_STATUSES.uninstalled]: { 
        type: STATUS_TYPES.WARNING,
        label: translationKeys.cameras.status.UNINSTALLED,
        description: translationKeys.cameras.statusDescriptions.UNINSTALLED,
        liveStreaming: STATUS_TYPES.ERROR,
        recording: STATUS_TYPES.ERROR,
        cloud: STATUS_TYPES.OK,
        adapter: STATUS_TYPES.OK,
        camera: [STATUS_TYPES.WARNING, translationKeys.cameras.warningDescriptions.USER_REMOVED],
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.UNINSTALLED_1,
        ],
        icon: UninstalledIcon,
    },
    [CAMERA_STATUSES.unreachable]: { 
        type: STATUS_TYPES.ERROR,
        label: translationKeys.cameras.status.UNREACHABLE,
        description: translationKeys.cameras.statusDescriptions.UNREACHABLE,
        liveStreaming: STATUS_TYPES.ERROR,
        recording: STATUS_TYPES.ERROR,
        cloud: STATUS_TYPES.OK,
        adapter: STATUS_TYPES.OK,
        camera: [STATUS_TYPES.ERROR, translationKeys.cameras.status.UNREACHABLE],
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.UNREACHABLE_1,
            translationKeys.cameras.statusTroubleshooting.UNREACHABLE_2,
            translationKeys.cameras.statusTroubleshooting.UNREACHABLE_3,
        ],
        icon: UnreachableIcon,
    },
    [CAMERA_STATUSES.unauthorised]: { 
        type: STATUS_TYPES.ERROR,
        label: translationKeys.cameras.status.UNAUTHORISED,
        description: translationKeys.cameras.statusDescriptions.UNAUTHORISED,
        liveStreaming: STATUS_TYPES.ERROR,
        recording: STATUS_TYPES.ERROR,
        cloud: STATUS_TYPES.OK,
        adapter: STATUS_TYPES.OK,
        camera: [STATUS_TYPES.ERROR, translationKeys.cameras.status.UNAUTHORISED],
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.UNAUTHORISED_1,
        ],
        icon: UnauthorisedIcon,
    },
    [CAMERA_STATUSES.cloudrecordingoff]: { 
        type: STATUS_TYPES.WARNING,
        label: translationKeys.cameras.status.CLOUD_RECORDING_OFF,
        description: translationKeys.cameras.statusDescriptions.CLOUD_RECORDING_OFF,
        liveStreaming: STATUS_TYPES.OK,
        recording: STATUS_TYPES.ERROR,
        cloud: [STATUS_TYPES.WARNING, translationKeys.cameras.warningDescriptions.DISABLED],
        adapter: STATUS_TYPES.OK,
        camera: STATUS_TYPES.OK,
        troubleshooting: [
            translationKeys.cameras.statusTroubleshooting.CLOUD_RECORDING_OFF_1,
        ],
        icon: CloudRecordingOffIcon,
    },
} as const;  